import { RouteRecordRaw } from "vue-router";
import { PAGE_TYPE, SEAL_AUTHORIZATION_TYPE } from "@supplier/meta";

import type { UserStore } from "@/stores/user";

import Layout from "@/views/layouts/index.vue";
import { type AccountStore } from "@/stores/account";

const meRouters: RouteRecordRaw[] = [
    {
        path: "/me",
        name: "Me",
        meta: {
            title: "账号中心",
            icon: "icon-zhanghaozhongxin"
        },
        component: Layout,
        redirect: "/me/info",
        children: [
            {
                path: "info",
                name: "Info",
                meta: {
                    title: "账号信息"
                },
                component: () => import("@/views/pages/me/info/index.vue")
            },
            {
                path: "dealingsCustomer",
                name: "DealingsCustomer",
                meta: {
                    title: "往来客户",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isSubAccount;
                    }
                },
                component: () => import("@/views/pages/me/dealingsCustomer/index.vue")
            },
            {
                path: "dealingsAdmin",
                name: "DealingsAdmin",
                meta: {
                    title: "往来供应链",
                    hidden: ({ userStore }: { userStore: UserStore }) => {
                        return !userStore.isSAAS;
                    }
                },
                component: () => import("@/views/pages/me/dealingsAdmin/index.vue")
            },
            {
                path: "safeSetting",
                name: "SafeSetting",
                meta: {
                    title: "安全",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isSubAccount || accountStore.isAssociated;
                    }
                },
                component: () => import("@/views/pages/me/safeSetting/index.vue")
            },
            {
                path: "safeSetting/updatePassword",
                name: "UpdatePassword",
                meta: { title: "更新密码", hidden: true, hideMenu: true, hideTabs: true },
                component: () => import("@/views/pages/me/safeSetting/updatePassword/index.vue")
            },
            {
                path: "authentication",
                name: "Authentication",
                meta: {
                    title: "认证",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isAssociated && !accountStore.isSubAccount;
                    }
                },
                component: () => import("@/views/pages/me/authentication/index.vue")
            },
            {
                path: "businessManage",
                name: "BusinessManage",
                component: () => import("@/views/pages/me/businessManage/businessManage.vue"),
                meta: {
                    title: "我的企业管理",
                    hidden: ({ userStore, accountStore }: { userStore: UserStore; accountStore: AccountStore }) => {
                        //判断是否已认证,已认证显示
                        return !userStore.userInfo.customer.applyState || accountStore.isSubAccount;
                    }
                }
            },
            {
                path: "subAccountManagement",
                name: "SubAccountManagement",
                meta: {
                    title: "子账号管理",
                    hidden: ({ userStore, accountStore }: { userStore: UserStore; accountStore: AccountStore }) => {
                        return (
                            (!accountStore.isRootAccount && !(accountStore.isAssociated && !accountStore.isSubAccount)) ||
                            (accountStore.isRootAccount && !userStore.isEnterpriseAuth)
                        );
                    }
                },
                component: () => import("@/views/pages/me/subAccountManagement/index.vue")
            },
            {
                path: "subAccountManagementInfo",
                name: "SubAccountManagementInfo",
                meta: {
                    title: "子账号信息",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return !accountStore.isSubAccount;
                    },
                    activeMenu: "SubAccountManagementInfo"
                },
                component: () => import("@/views/pages/me/subAccountManagement/detail.vue")
            },
            {
                path: "subAccountManagement/detail",
                name: "SubAccountManagementDetail",
                meta: {
                    title: "子账号详情",
                    hidden: true,
                    activeMenu: "SubAccountManagement"
                },
                component: () => import("@/views/pages/me/subAccountManagement/detail.vue")
            },
            {
                path: "subAccountManagement/edit",
                name: "SubAccountManagementEdit",
                meta: { title: "编辑子账号", hidden: true, activeMenu: "SubAccountManagement" },
                component: () => import("@/views/pages/me/subAccountManagement/edit.vue")
            },
            {
                path: "subAccountManagement/invitationHistory",
                name: "InvitationHistory",
                meta: { title: "邀请记录", hidden: true, activeMenu: "SubAccountManagement" },
                component: () => import("@/views/pages/me/subAccountManagement/invitationHistory/index.vue")
            },
            {
                path: "subAccountManagement/applyJoinHistory",
                name: "ApplyJoinHistory",
                meta: { title: "申请记录", hidden: true, activeMenu: "SubAccountManagement" },
                component: () => import("@/views/pages/me/subAccountManagement/applyJoinHistory/index.vue")
            },
            {
                path: "sealManagement",
                name: "SealManagement",
                meta: {
                    title: "印章管理",
                    hidden: ({ accountStore, userStore }: { accountStore: AccountStore; userStore: UserStore }) => {
                        return (
                            !(
                                (accountStore.isRootAccount || accountStore.isAssociated) &&
                                userStore.userInfo?.mainBody?.sealAuthorization === SEAL_AUTHORIZATION_TYPE.YES.code
                            ) || accountStore.isSubAccount
                        );
                    }
                },
                component: () => import("@/views/pages/me/sealManagement/index.vue")
            },
            {
                path: "sealManagement/detail",
                name: "SealManagementDetail",
                meta: { title: "印章详情", hidden: true, activeMenu: "SealManagement" },
                component: () => import("@/views/pages/me/sealManagement/detail.vue")
            },
            {
                path: "sealManagement/add",
                name: "SealManagementAdd",
                meta: {
                    title: "新增印章",
                    hidden: true,
                    KeepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "SealManagement"
                },
                component: () => import("@/views/pages/me/sealManagement/add.vue")
            },
            {
                path: "sealManagement/edit",
                name: "SealManagementEdit",
                meta: {
                    title: "编辑印章",
                    hidden: true,
                    KeepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "SealManagement"
                },
                component: () => import("@/views/pages/me/sealManagement/edit.vue")
            },
            {
                path: "invitationRecord",
                name: "InvitationRecord",
                meta: {
                    title: "受邀记录",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isSubAccount || accountStore.isAssociated;
                    }
                },
                component: () => import("@/views/pages/me/invitationRecord/index.vue")
            },
            {
                path: "SMSManagement",
                name: "SMSManagement",
                meta: {
                    title: "短信管理",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return !accountStore.isRootAccount && !(accountStore.isAssociated && !accountStore.isSubAccount);
                    }
                },
                component: () => import("@/views/pages/me/SMSManagement/index.vue")
            },
            {
                path: "SMSManagement/edit",
                name: "SMSManagementEdit",
                meta: { title: "短信管理", hidden: true, activeMenu: "SMSManagement" },
                component: () => import("@/views/pages/me/SMSManagement/edit.vue")
            },
            {
                path: "invoiceHeader",
                name: "InvoiceHeader",
                meta: {
                    title: "发票抬头",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isSubAccount;
                    }
                },
                component: () => import("@/views/pages/me/invoice/index.vue")
            },
            {
                path: "invoiceHeader/add",
                name: "InvoiceHeaderAdd",
                meta: {
                    title: "新增发票抬头",
                    hidden: true,
                    keepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "InvoiceHeader"
                },
                component: () => import("@/views/pages/me/invoice/add.vue")
            },
            {
                path: "invoice/edit",
                name: "InvoiceHeaderEdit",
                meta: {
                    title: "修改发票抬头",
                    hidden: true,
                    keepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "InvoiceHeader"
                },
                component: () => import("@/views/pages/me/invoice/add.vue")
            },
            {
                path: "address",
                name: "Address",
                meta: {
                    title: "我的地址",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isSubAccount;
                    }
                },
                component: () => import("@/views/pages/me/address/index.vue")
            },
            {
                path: "address/add",
                name: "AddressAdd",
                meta: {
                    title: "新增地址",
                    hidden: true,
                    keepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "Address"
                },
                component: () => import("@/views/pages/me/address/add.vue")
            },
            {
                path: "address/edit",
                name: "AddressEdit",
                meta: {
                    title: "修改地址",
                    hidden: true,
                    keepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "Address"
                },
                component: () => import("@/views/pages/me/address/add.vue")
            },
            {
                path: "bankAccount",
                name: "BankAccount",
                meta: {
                    title: "银行账户",
                    hidden: ({ accountStore }: { accountStore: AccountStore }) => {
                        return accountStore.isSubAccount;
                    }
                },
                component: () => import("@/views/pages/me/bankAccount/index.vue")
            },
            {
                path: "bankAccount/add",
                name: "BankAccountAdd",
                meta: {
                    title: "新增银行账户",
                    hidden: true,
                    keepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "BankAccount"
                },
                component: () => import("@/views/pages/me/bankAccount/edit/index.vue")
            },
            {
                path: "bankAccount/edit",
                name: "BankAccountEdit",
                meta: {
                    title: "修改银行账户",
                    hidden: true,
                    keepAlive: true,
                    pageType: PAGE_TYPE.CACHE_FORM.code,
                    activeMenu: "BankAccount"
                },
                component: () => import("@/views/pages/me/bankAccount/edit/index.vue")
            }
        ]
    }
];

export default meRouters;
